import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

const API_BASE = '/api';

function safeParseJSON(maybeJSON) {
  if (typeof maybeJSON !== 'string') return null;
  try {
    return JSON.parse(maybeJSON);
  } catch {
    return null;
  }
}

function fileUrlFromAnswer(answer) {
  if (!answer) return null;

  const parsed = safeParseJSON(answer);
  if (parsed && parsed.url) {
    return parsed.url.startsWith('http')
      ? parsed.url
      : `http://206.189.134.179:8082${parsed.url}`;
  }

  if (typeof answer === 'string') {
    if (answer.startsWith('http')) return answer;
    if (answer.startsWith('/uploads/')) return `http://206.189.134.179:8082${answer}`;
  }

  return null;
}

function getAbsoluteFileUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return url;
}

function getDownloadUrl(filename) {
  return `/api/files/${filename}/download`;
}

function extractFirstLast(submission) {
  let first = '';
  let last = '';

  const compound = submission.responses.find(r => r.fieldType === 'compound_name');
  if (compound) {
    first = compound.firstName || '';
    last = compound.lastName || '';
  }

  submission.responses.forEach(r => {
    const q = String(r.questionText || '');
    if (!first && /(^|\W)first\s*name(\W|$)/i.test(q)) first = String(r.answer || '');
    if (!last  && /(^|\W)last\s*name(\W|$)/i.test(q))  last  = String(r.answer || '');
  });

  return { first, last };
}

function extractPhone(submission) {
  let phone = '';
  submission.responses.forEach(r => {
    const q = String(r.questionText || '');
    if (/phone|mobile|contact\s*number/i.test(q)) {
      if (!phone) phone = String(r.answer || '');
    }
  });
  return phone;
}

function extractEmail(submission) {
  let email = '';
  submission.responses.forEach(r => {
    const q = String(r.questionText || '');
    if (/email/i.test(q)) {
      if (!email) email = String(r.answer || '');
    }
  });
  return email;
}

const AdminPanel = () => {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [fileViewerData, setFileViewerData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadSubmissions();
    loadStats();
    // eslint-disable-next-line
  }, [currentPage, search, dateFrom, dateTo]);

  const checkAuth = async () => {
    try {
      const r = await axios.get(`${API_BASE}/admin/whoami`, { withCredentials: true });
      if (!r.data?.isAuthenticated) {
        navigate('/login');
        return false;
      }
      return true;
    } catch {
      navigate('/login');
      return false;
    }
  };

  const loadSubmissions = async () => {
    const ok = await checkAuth();
    if (!ok) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(search && { search }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo })
      });

      const response = await axios.get(`${API_BASE}/admin/responses?${params}`, {
        withCredentials: true
      });

      setSubmissions(response.data.submissions || []);
      setTotalPages(response.data.totalPages);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to load submissions');
      }
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE}/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadSubmissions();
  };

  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const exportToCSV = () => {
    if (submissions.length === 0) return;

    let csvContent = "Session ID,Submitted At,Question,Answer\n";

    submissions.forEach(submission => {
      submission.responses.forEach(response => {
        let answerText = response.answer;

        if (response.fieldType === 'file') {
          const url = fileUrlFromAnswer(response.answer);
          if (url) answerText = url;
        }

        const row = [
          submission.sessionId,
          submission.submittedAt ? new Date(submission.submittedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '',
          `"${String(response.questionText).replace(/"/g, '""')}"`,
          `"${String(answerText ?? '').replace(/"/g, '""')}"`,
        ].join(',');
        csvContent += row + "\n";
      });
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form_submissions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportIndividualCSV = async (sessionId) => {
    try {
      const response = await axios.get(`${API_BASE}/admin/export/${sessionId}/csv`, {
        withCredentials: true,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submission_${sessionId}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        alert('Failed to export CSV: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const exportIndividualPDF = async (sessionId) => {
    try {
      const response = await axios.get(`${API_BASE}/admin/export/${sessionId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submission_${sessionId}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        alert('Failed to export PDF: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const exportIndividualJSON = async (sessionId) => {
    try {
      const response = await axios.get(`${API_BASE}/admin/export/${sessionId}/json`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submission_${sessionId}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else alert('Failed to export JSON: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="admin-panel">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-title">
          <h1>Form Responses Dashboard</h1>
          <div className="admin-actions">
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
            <button onClick={() => navigate('/')} className="back-btn">
              Back to Home
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{stats.total_completed || 0}</div>
            <div className="stat-label">Total Submissions</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.today_completed || 0}</div>
            <div className="stat-label">Today</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.week_completed || 0}</div>
            <div className="stat-label">This Week</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {stats.avg_completion_minutes ? Math.round(stats.avg_completion_minutes) : 0}m
            </div>
            <div className="stat-label">Avg. Time</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="admin-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search responses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <input
            type="date"
            placeholder="From Date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="date-input"
          />
          <input
            type="date"
            placeholder="To Date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="date-input"
          />
          <button type="submit" className="search-btn">Search</button>
          <button type="button" onClick={clearFilters} className="clear-btn">Clear</button>
          <button type="button" onClick={exportToCSV} className="export-btn">Export CSV</button>
        </form>
      </div>

      {/* Submissions Table */}
      <div className="submissions-container">
        {loading ? (
          <div className="loading">Loading submissions...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : submissions.length === 0 ? (
          <div className="no-data">No submissions found matching your criteria.</div>
        ) : (
          <>
            <div className="submissions-table">
              <table>
                <thead>
                  <tr>
                    <th>Session ID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Submitted Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => {
                    const { first, last } = extractFirstLast(submission);
                    const phone = extractPhone(submission);
                    const email = extractEmail(submission);

                    return (
                      <tr key={submission.sessionId}>
                        <td className="session-id">{submission.submissionNumber? `SUB-${String(submission.submissionNumber).padStart(2, '0')}`: '-'}</td>
                        <td>{first || '-'}</td>
                        <td>{last || '-'}</td>
                        <td>{phone || '-'}</td>
                        <td>{email || '-'}</td>
                        <td>{submission.submittedAt ? new Date(submission.submittedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => setSelectedSubmission(submission)}
                              className="view-btn"
                            >
                              View
                            </button>
                            <button
                              onClick={() => exportIndividualCSV(submission.sessionId)}
                              className="export-btn-small"
                              title="Export as CSV"
                            >
                              CSV
                            </button>
                            <button
                              onClick={() => exportIndividualPDF(submission.sessionId)}
                              className="export-btn-small"
                              title="Export as PDF"
                            >
                              PDF
                            </button>
                            <button
                              onClick={() => exportIndividualJSON(submission.sessionId)}
                              className="export-btn-small"
                              title="Export as JSON"
                            >
                              JSON
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="modal-overlay" onClick={() => setSelectedSubmission(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Submission Details</h2>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="close-btn"
              >
                x
              </button>
            </div>
            <div className="modal-body">
              <div className="submission-info">
                <p><strong>Session ID:</strong> {selectedSubmission.sessionId}</p>
                <p><strong>Submitted:</strong> {selectedSubmission.submittedAt ? new Date(selectedSubmission.submittedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</p>
                <p><strong>Total Questions:</strong> {selectedSubmission.responses.length}</p>
              </div>
              <div className="responses-list">
                {selectedSubmission.responses.map((response, index) => {
                  const fileUrl = response.fieldType === 'file'
                    ? fileUrlFromAnswer(response.answer)
                    : null;

                  return (
                    <div key={index} className="response-item">
                      <div className="question-number">Q{response.questionNumber}</div>
                      <div className="question-content">
                        <strong>{response.questionText}</strong>
                        {response.fieldType === 'file' && response.answer ? (
                          (() => {
                            try {
                              const parsedData = safeParseJSON(response.answer);
                              if (Array.isArray(parsedData)) {
                                return (
                                  <div className="multiple-files-preview">
                                    <div className="files-count">{parsedData.length} file(s) uploaded</div>
                                    {parsedData.map((fileData, fileIndex) => (
                                      <div key={fileIndex} className="file-preview">
                                        <div className="file-info">
                                          <strong>File {fileIndex + 1}:</strong> {fileData.originalName}
                                          <br />
                                          <small>Size: {(fileData.size / 1024 / 1024).toFixed(2)} MB</small>
                                        </div>
                                        <div className="file-actions">
                                          <button
                                            onClick={() => setFileViewerData({
                                              url: getAbsoluteFileUrl(fileData.url),
                                              filename: fileData.originalName
                                            })}
                                            className="file-view-btn"
                                          >
                                            View
                                          </button>
                                          <button
                                            onClick={() => {
                                              const link = document.createElement('a');
                                              link.href = getDownloadUrl(fileData.filename);
                                              link.download = fileData.originalName;
                                              document.body.appendChild(link);
                                              link.click();
                                              document.body.removeChild(link);
                                            }}
                                            className="file-download-btn"
                                          >
                                            Download
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              } else if (parsedData && parsedData.originalName) {
                                return (
                                  <div className="file-preview">
                                    <div className="file-info">
                                      <strong>File:</strong> {parsedData.originalName}
                                      <br />
                                      <small>Size: {(parsedData.size / 1024 / 1024).toFixed(2)} MB</small>
                                    </div>
                                    <div className="file-actions">
                                      <button
                                        onClick={() => setFileViewerData({
                                          url: getAbsoluteFileUrl(parsedData.url),
                                          filename: parsedData.originalName
                                        })}
                                        className="file-view-btn"
                                      >
                                        View
                                      </button>
                                      <button
                                        onClick={() => {
                                          const link = document.createElement('a');
                                          link.href = getDownloadUrl(parsedData.filename);
                                          link.download = parsedData.originalName;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                        className="file-download-btn"
                                      >
                                        Download
                                      </button>
                                    </div>
                                  </div>
                                );
                              }
                            } catch (e) {
                              // fallback
                            }
                            return (
                              <p className="answer">
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View / Download File
                                </a>
                              </p>
                            );
                          })()
                        ) : (
                          <p className="answer">{String(response.answer ?? '')}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {fileViewerData && (
        <div className="modal-overlay" onClick={() => setFileViewerData(null)}>
          <div className="modal-content file-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{fileViewerData.filename || 'File Preview'}</h2>
              <button
                onClick={() => setFileViewerData(null)}
                className="close-btn"
              >
                x
              </button>
            </div>
            <div className="modal-body file-viewer-body">
              {fileViewerData.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img
                  src={fileViewerData.url}
                  alt={fileViewerData.filename}
                  className="file-viewer-image"
                />
              ) : fileViewerData.url.match(/\.pdf$/i) ? (
                <iframe
                  src={fileViewerData.url}
                  className="file-viewer-pdf"
                  title={fileViewerData.filename}
                />
              ) : (
                <div className="file-viewer-unsupported">
                  <p>Preview not available for this file type.</p>
                  <p>Please download the file to view it.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = fileViewerData.url;
                  link.download = fileViewerData.filename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="nav-btn submit-btn"
              >
                Download
              </button>
              <button
                onClick={() => setFileViewerData(null)}
                className="nav-btn cancel-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
